import { useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Modal } from '../../shared/ui'
import { Usuarios, UsuarioForm, UsuarioGrupos } from '../../modules/usuarios'

import { Grupos, GrupoForm, GrupoUsuarios, GrupoPermisos } from '../../modules/grupos'
import { Modulos, ModuloForm } from '../../modules/modulos'
import { Companias, CompaniaForm } from '../../modules/companias'
import { SucursalForm, Sucursales } from '../../modules/sucursales'
import { Login } from '../../modules/auth'
import { Sedes, SedeForm } from '../../modules/sedes'
import { ParametroForm, Parametros } from '../../modules/parametros'

import { cambiarColores, limpiarLocalStorage, obtenerLocalStorage } from '../../shared/utils/funciones'
import { ScrollToTop } from '../components/ScrollToTop'
import { CONST_KEY_USUARIO_AUTENTICADO, CONST_PARAMETRO_SISTEMA_SEGURIDAD, CONST_ROL_MASTER } from '../constants/constanst-valores'
import { SinAcceso } from '../components/SinAcceso'
import { CONST_RUTA_LOGIN, CONST_RUTA_SIN_ACCESO } from '../constants/constants-rutas-codigos'
import { DataContext } from '../providers/DataContext'
import { InicioSeguridad } from '../components/InicioSeguridad'
import { rutasCodigosPrograma } from '../constants/constanst-arreglo-rutas-codigo'

import { ConexionExterna } from '../../modules/auth'
import { IniciandoSesion } from '../../modules/auth'
import { SideBar } from "../components/layout"
import { Auditoria } from "../../modules/auditoria"

const routesConfig = [
    {
        path: "/seguridad",
        component: InicioSeguridad,
    },
    {
        path: "/seguridad/usuarios/*",
        component: Usuarios,
        children: [
            {
                path: "agregar",
                component: UsuarioForm,
            },
            {
                path: "grupos",
                component: UsuarioGrupos,
            },
            {
                path: "editar",
                component: UsuarioForm,
            },
        ],
    },
    {
        path: "/seguridad/grupos/*",
        component: Grupos,
        children: [
            {
                path: "agregar",
                component: GrupoForm,
            },
            {
                // "Usuarios del grupo": desde este modal se abre "Añadir usuarios" como
                // modal ANIDADO por ESTADO (dentro de GrupoUsuarios), no por ruta, para que
                // el modal padre no se desmonte y el cierre sea "por niveles".
                path: "usuarios",
                component: GrupoUsuarios,
            },
            {
                path: "permisos",
                component: GrupoPermisos,
            },
            {
                path: "editar",
                component: GrupoForm,
            },
        ],
    },
    {
        path: "/seguridad/modulos/*",
        component: Modulos,
        children: [
            {
                path: "agregar",
                component: ModuloForm,
            },
            {
                path: "editar",
                component: ModuloForm,
            },
        ],
    },
    {
        path: "/seguridad/companias/*",
        component: Companias,
        children: [
            {
                path: "agregar",
                component: CompaniaForm,
            },
            {
                path: "editar",
                component: CompaniaForm,
            },
        ],
    },
    {
        path: "/seguridad/sucursales/*",
        component: Sucursales,
        children: [
            {
                path: "agregar",
                component: SucursalForm,
            },
            {
                path: "editar",
                component: SucursalForm,
            },
        ],
    },
    {
        path: "/seguridad/sedes/*",
        component: Sedes,
        children: [
            {
                path: "agregar",
                component: SedeForm,
            },
            {
                path: "editar",
                component: SedeForm,
            },
        ],
    },
    {
        path: "/seguridad/parametros/*",
        component: Parametros,
        children: [
            {
                path: "agregar",
                component: ParametroForm,
            },
            {
                path: "editar",
                component: ParametroForm,
            },
        ],
    },
    // Agrega configuraciones para otros componentes aquí
    {
        path: "/seguridad/auditoria/*",
        component: Auditoria,

    },
];

// Envuelve el contenido de una sub-ruta (agregar/editar/usuarios/permisos/...) en un
// modal. El listado del módulo permanece montado de fondo (oscurecido por el overlay).
// Cierre por la "X" (o por el botón "Cancelar" del propio contenido) -> navigate(-1),
// que regresa a la ruta anterior conservando su estado (listado o modal padre).
// dismissable=false: no cierra por Esc / click fuera, para no perder datos sin guardar.
const ModalRuta = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    return (
        <Modal open size="xl" showClose dismissable={false} onOpenChange={(o) => { if (!o) navigate(-1) }}>
            {children}
        </Modal>
    )
}

export const AppRouter = () => {
    const usuarioAutenticado = obtenerLocalStorage(CONST_KEY_USUARIO_AUTENTICADO);

    //Obtener los estilos de la pagina de cada compania
    const estilosCompania = usuarioAutenticado?.estilo;

    const { getRutasDeAcceso, listaRutasDeAcceso, rutasEstado } = useContext(DataContext)

    const location = useLocation();

    const ruta = location.pathname.split("/").pop();

    const url_pagina = location?.pathname

    const [mostrarPaginaAutenticado, setMostrarPaginaAutenticado] = useState(false);

    const [isLoading, setIsLoading] = useState(true)

    const navigate = useNavigate()

    // OPT-GUARD-NAV (ítem 7): el guard de navegación deriva el acceso EN MEMORIA de RutaPlana
    // (`listaRutasDeAcceso`) —la MISMA fuente que ya gobierna la visibilidad de botones de cada
    // página— en vez de un round-trip BLOQUEANTE a VerificarUseMod en cada navegación.
    // Equivalente por construcción: el backend deriva VerificarUseMod y RutaPlana de la misma
    // fuente (MASTER→acceso total en ambos; no-master→grupos→módulos en ambos), así que
    // `some(codigo_programa===codigo)` ≡ `verificarUsuario(codigo).valor`. La AUTORIDAD real es
    // el SERVIDOR (S0.1 enforce): si el cliente mostrara una pantalla no permitida, cualquier
    // operación responde 401/403 en el backend. Esto elimina el doble-guard (server-por-nav +
    // memoria-por-acción) unificándolo a una sola fuente en memoria, sin bloquear el render.
    const rol = usuarioAutenticado?.tipo

    // Dispara la carga (CACHEADA por token en el DataProvider) de RutaPlana. En navegaciones
    // posteriores es un no-op (ya está en memoria) → sin round-trip por navegación.
    useEffect(() => {
        if (usuarioAutenticado?.token) {
            getRutasDeAcceso(usuarioAutenticado?.token, CONST_PARAMETRO_SISTEMA_SEGURIDAD)
        }
        // efecto intencional (no re-ejecutar por deps faltantes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url_pagina])

    // Deriva el acceso de la ruta actual desde RutaPlana en memoria + el estado de su carga.
    useEffect(() => {
        if (!usuarioAutenticado?.token) return
        if (url_pagina === "/") { setMostrarPaginaAutenticado(true); setIsLoading(false); return }
        if (rutasEstado === "sesion-vencida") {
            // Token inválido/expirado (detectado por la llamada a RutaPlana): cerrar sesión —
            // mismo comportamiento que antes disparaba VerificarUseMod ("El token es invalido").
            limpiarLocalStorage(CONST_KEY_USUARIO_AUTENTICADO)
            navigate(CONST_RUTA_LOGIN)
            return
        }
        if (rutasEstado === "cargando") { setIsLoading(true); return }
        // rutasEstado === "cargado": derivar el acceso (fail-closed como antes).
        const modulo = rutasCodigosPrograma.find(r => String(r.ruta) === String(url_pagina))
        const codigo = modulo?.codigo || ""
        // MASTER: acceso total (idéntico al bypass de VerificarUseMod). Código vacío = ruta sin
        // mapeo → SinAcceso (idéntico al 400 del backend). No-master: pertenencia en RutaPlana.
        const autorizado = codigo === ""
            ? false
            : (rol === CONST_ROL_MASTER || (listaRutasDeAcceso || []).some((r: any) => String(r.codigo_programa) === String(codigo)))
        setMostrarPaginaAutenticado(autorizado)
        setIsLoading(false)
        // efecto intencional (deps explícitas)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url_pagina, rutasEstado, listaRutasDeAcceso])

    useEffect(() => {
        // Guardar los colores al cargar la pagina
        if (estilosCompania) {
            const colores = [
                { name: '--color-fondo-menu-primario', color: estilosCompania?.fondo_menu },
                { name: '--color-texto-nav-item', color: estilosCompania?.text_opciones },
                { name: '--color-fondo-nav-item-activo', color: estilosCompania?.fond_opc_activo },
                { name: '--color-texto-nav-item-activo', color: estilosCompania?.text_opc_activo },
                { name: '--color-button-primario', color: estilosCompania?.boton_primario },
                { name: '--color-button-primario-hover', color: estilosCompania?.boton_primario_activo },
                { name: '--color-button-primario-text', color: estilosCompania?.text_boton_primario },
                { name: '--color-button-secundario', color: estilosCompania?.boton_secundario },
                { name: '--color-button-secundario-hover', color: estilosCompania?.boton_secundario_activo },
                { name: '--color-button-secundario-text', color: estilosCompania?.text_boton_secundario },
            ];
            cambiarColores(colores);
        } else {
            // const colores = [
            //     { name: '--color-fondo-menu-primario', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_FONDO_MENU_PRIMARIO },
            //     { name: '--color-texto-nav-item', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_TEXTO_NAV_ITEM },
            //     { name: '--color-fondo-nav-item-activo', color: configColoresCompaniaSUPERADMIN?.CONST_FONDO_NAV_ITEM_ACTIVO },
            //     { name: '--color-texto-nav-item-activo', color: configColoresCompaniaSUPERADMIN?.CONST_TEXTO_NAV_ITEM_ACTIVO },
            //     { name: '--color-button-primario', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_PRIMARIO },
            //     { name: '--color-button-primario-hover', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_PRIMARIO_HOVER },
            //     { name: '--color-button-primario-text', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_PRIMARIO_TEXT },
            //     { name: '--color-button-secundario', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_SECUNDARIO },
            //     { name: '--color-button-secundario-hover', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_SECUNDARIO_HOVER },
            //     { name: '--color-button-secundario-text', color: configColoresCompaniaSUPERADMIN?.CONST_COLOR_BUTTON_SECUNDARIO_TEXT},
            // ];
            // cambiarColores(colores);
        }

        // getCompaniasYSedesPorUsuario(usuarioAutenticado?.token)
        // efecto intencional (no re-ejecutar por deps faltantes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return usuarioAutenticado?.token != null ? (
        (mostrarPaginaAutenticado) ? (
            <>
                <ScrollToTop />
                <SideBar ruta={ruta}>
                    <Routes>
                        <Route path={CONST_RUTA_LOGIN} element={<Login />} />
                        {routesConfig.map((route, index) => (
                            <Route
                                key={index}
                                path={route.path}
                                element={
                                    <>
                                        {/* Listado del módulo: SIEMPRE montado (fondo del modal).
                                            Al abrir una sub-ruta no se desmonta -> conserva filtros. */}
                                        <route.component />
                                        {route.children && (
                                            <Routes>
                                                {route.children.map((childRoute, childIndex) => {
                                                    const Contenido: any = childRoute.component
                                                    return (
                                                        <Route
                                                            key={childIndex}
                                                            path={childRoute?.path}
                                                            element={
                                                                <ModalRuta>
                                                                    <Contenido />
                                                                </ModalRuta>
                                                            }
                                                        />)
                                                })}
                                            </Routes>
                                        )}
                                    </>
                                }
                            />
                        ))}

                        <Route path="/*" element={<SinAcceso />} />
                    </Routes>
                </SideBar>

            </>
        ) : (
            <>
                <ScrollToTop />
                <SideBar ruta={ruta}>
                    {isLoading ? (
                        <div className="h-100 d-flex justify-content-center align-items-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : <SinAcceso />}
                </SideBar>
            </>
        )
    ) : (
        <>
            <ScrollToTop />
            <Routes>
                <Route path={CONST_RUTA_LOGIN} element={<Login />} />

                <Route path="/conexionExterna/:token" element={<ConexionExterna />}></Route>
                <Route path="/conectando" element={<IniciandoSesion />}></Route>
                <Route path={CONST_RUTA_SIN_ACCESO} element={<SinAcceso />} />
                <Route path="/*" element={<Navigate to="/" />} />
            </Routes>

        </>

    )

};

